/**
 * TargetingSection — reusable targeting block used by all per-objective adset forms.
 * Includes: Locations, Age range, Genders, Interests, Custom audiences, Advantage audience toggle.
 */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { facebookAudiencesService, MetaCustomAudience } from '@/services/facebook-audiences.service';
import { FormField } from '@/components/FormField';
import { LocationsPicker, SelectedLocation } from './LocationsPicker';
import { InterestsPicker, SelectedInterest } from './InterestsPicker';
import { ChevronDown, ChevronUp, ExternalLink, Users } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

export interface TargetingValue {
  locations: SelectedLocation[];
  age_min: number;
  age_max: number;
  genders: number[];   // 1=male, 2=female, [] = all
  interests: SelectedInterest[];
  custom_audiences: string[];
  excluded_custom_audiences: string[];
  advantage_audience: 0 | 1;
}

export function defaultTargeting(): TargetingValue {
  return {
    locations: [],
    age_min: 18,
    age_max: 55,
    genders: [],
    interests: [],
    custom_audiences: [],
    excluded_custom_audiences: [],
    advantage_audience: 0,
  };
}

interface Props {
  value: TargetingValue;
  onChange: (v: TargetingValue) => void;
  disabled?: boolean;
}

// Simple dual-range via two stacked inputs. Both sides clamp relative to each
// other so the user can never produce age_min > age_max (which would push an
// invalid targeting payload to Meta).
//
// `lockedMax` forces the max to 65 — used when Advantage+ audience is on, since
// Meta rejects age_max < 65 in that mode.
function AgeRange({
  min,
  max,
  onChange,
  lockedMax,
}: {
  min: number;
  max: number;
  onChange: (min: number, max: number) => void;
  lockedMax?: boolean;
}) {
  const clampMin = (v: number) => Math.min(Math.max(13, v), max);
  const clampMax = (v: number) => Math.max(Math.min(65, v), min);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <label className="block text-2xs text-text-muted mb-1">Min</label>
        <input
          type="number" min={13} max={max} value={min}
          onChange={e => onChange(clampMin(+e.target.value || 13), max)}
          className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-sm text-text-primary focus:outline-none focus:border-primary"
        />
      </div>
      <span className="text-text-muted mt-4 shrink-0">–</span>
      <div className="flex-1 min-w-0">
        <label className="block text-2xs text-text-muted mb-1">Max</label>
        <input
          type="number" min={min} max={65} value={max}
          disabled={lockedMax}
          onChange={e => onChange(min, clampMax(+e.target.value || 65))}
          className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-sm text-text-primary focus:outline-none focus:border-primary disabled:bg-surface-alt disabled:text-text-muted disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
}

export const TargetingSection: React.FC<Props> = ({ value, onChange, disabled }) => {
  const [audiences, setAudiences] = useState<MetaCustomAudience[]>([]);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    facebookAudiencesService.list().then(r => setAudiences(r.items)).catch(() => undefined);
  }, []);

  const set = <K extends keyof TargetingValue>(k: K, v: TargetingValue[K]) => onChange({ ...value, [k]: v });

  const toggleGender = (code: number) => {
    const next = value.genders.includes(code)
      ? value.genders.filter(g => g !== code)
      : [...value.genders, code];
    set('genders', next);
  };

  return (
    <div className="space-y-5">
      {/* Locations */}
      <FormField label="Locations">
        <LocationsPicker
          selected={value.locations}
          onChange={locs => set('locations', locs)}
        />
      </FormField>

      {/* Age + Gender */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormField
          label="Age range"
          hintBelow
          hint={
            value.advantage_audience === 1
              ? 'Advantage+ requires max age 65 — Meta uses your range as a suggestion only.'
              : undefined
          }
        >
          <AgeRange
            min={value.age_min}
            max={value.age_max}
            lockedMax={value.advantage_audience === 1}
            onChange={(min, max) => onChange({ ...value, age_min: min, age_max: max })}
          />
        </FormField>

        <FormField label="Gender">
          <div className="flex gap-1.5 mt-1">
            {[{ code: 0, label: 'All' }, { code: 1, label: 'Men' }, { code: 2, label: 'Women' }].map(g => {
              const active = g.code === 0 ? value.genders.length === 0 : value.genders.includes(g.code);
              return (
                <button
                  key={g.code}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (g.code === 0) set('genders', []);
                    else toggleGender(g.code);
                  }}
                  className={clsx(
                    'flex-1 rounded-lg border py-1.5 text-xs font-medium transition-colors',
                    active ? 'border-primary bg-primary-soft text-primary' : 'border-border text-text-muted hover:border-border-strong',
                  )}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </FormField>
      </div>

      {/* Interests */}
      <FormField label="Interests">
        <InterestsPicker
          selected={value.interests}
          onChange={interests => set('interests', interests)}
        />
      </FormField>

      {/* Advanced: custom audiences + advantage */}
      <div>
        <button
          type="button"
          onClick={() => setShowMore(v => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
        >
          {showMore ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          Advanced targeting options
        </button>

        {showMore && (
          <div className="mt-4 space-y-4">
            {/* Custom audiences — three-way per row: off / include / exclude.
                Excluded audiences map to `excluded_custom_audiences` on the
                Meta payload (e.g. "exclude past customers"). The data model
                already supported this; the UI just didn't surface it. */}
            <FormField
              label="Custom audiences"
              hintBelow
              hint="Include reaches matching users; Exclude removes them."
            >
              {audiences.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-surface-alt px-3 py-3 text-xs text-text-muted">
                  <div className="font-medium text-text-secondary mb-0.5">No custom audiences yet</div>
                  <div>
                    Build them from your CRM contacts, lookalikes, or website visitors first.{' '}
                    <RouterLink
                      to="/facebook/audiences"
                      className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
                    >
                      Manage audiences <ExternalLink size={10} />
                    </RouterLink>
                  </div>
                </div>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto rounded-lg border border-border p-2">
                  {audiences.map(a => {
                    const included = value.custom_audiences.includes(a.metaAudienceId);
                    const excluded = value.excluded_custom_audiences.includes(a.metaAudienceId);
                    const state: 'off' | 'include' | 'exclude' = included ? 'include' : excluded ? 'exclude' : 'off';
                    const setState = (next: 'off' | 'include' | 'exclude') => {
                      // Strip from both lists, then add to the target one.
                      const inc = value.custom_audiences.filter(x => x !== a.metaAudienceId);
                      const exc = value.excluded_custom_audiences.filter(x => x !== a.metaAudienceId);
                      onChange({
                        ...value,
                        custom_audiences: next === 'include' ? [...inc, a.metaAudienceId] : inc,
                        excluded_custom_audiences: next === 'exclude' ? [...exc, a.metaAudienceId] : exc,
                      });
                    };
                    return (
                      <div key={a.metaAudienceId} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface-alt">
                        <span
                          className={clsx(
                            'flex-1 truncate text-xs',
                            state === 'exclude' ? 'text-danger line-through' : 'text-text-primary',
                          )}
                          title={a.name}
                        >
                          {a.name}
                        </span>
                        {a.approximateCount && (
                          <span className="text-2xs text-text-muted shrink-0">~{a.approximateCount.toLocaleString()}</span>
                        )}
                        <div className="inline-flex shrink-0 rounded-md border border-border bg-surface p-0.5">
                          {([
                            { v: 'off', l: 'Off' },
                            { v: 'include', l: 'Include' },
                            { v: 'exclude', l: 'Exclude' },
                          ] as const).map(opt => (
                            <button
                              key={opt.v}
                              type="button"
                              onClick={() => setState(opt.v)}
                              className={clsx(
                                'rounded px-2 py-0.5 text-2xs font-medium transition-colors',
                                state === opt.v
                                  ? opt.v === 'exclude'
                                    ? 'bg-danger-soft text-danger'
                                    : opt.v === 'include'
                                    ? 'bg-primary-soft text-primary'
                                    : 'bg-surface-alt text-text-secondary'
                                  : 'text-text-muted hover:text-text-primary',
                              )}
                            >
                              {opt.l}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </FormField>

            {/* Advantage audience toggle. Toggling ON forces age_max to 65
                because Meta rejects lower max ages in this mode. */}
            <label className="flex items-start gap-3 rounded-xl border border-border bg-surface-alt/40 p-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 accent-primary"
                checked={value.advantage_audience === 1}
                onChange={e => {
                  const on = e.target.checked;
                  onChange({
                    ...value,
                    advantage_audience: on ? 1 : 0,
                    age_max: on ? 65 : value.age_max,
                  });
                }}
              />
              <div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
                  <Users size={13} className="text-primary" />
                  Advantage audience
                </div>
                <p className="text-2xs text-text-muted mt-0.5">
                  Meta automatically finds the best audience using your page info and ad creative signals. Recommended for most campaigns.
                  <br />
                  <span className="italic">Note: Meta requires max age 65 in this mode and treats your range as a suggestion.</span>
                </p>
              </div>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

/** Convert TargetingValue to the API payload shape */
export function buildTargetingPayload(t: TargetingValue) {
  const countries = t.locations.filter(l => l.type === 'country').map(l => l.code);
  const cities    = t.locations.filter(l => l.type === 'city').map(l => ({ key: l.code }));
  const regions   = t.locations.filter(l => l.type === 'region').map(l => ({ key: l.code }));

  const geo_locations: Record<string, unknown> = {};
  if (countries.length) geo_locations.countries = countries;
  if (cities.length)    geo_locations.cities    = cities;
  if (regions.length)   geo_locations.regions   = regions;

  // Defensive: Meta rejects age_max < 65 when advantage_audience is on. The UI
  // already auto-clamps when toggling, but this guards against stale form data
  // (e.g. a saved draft with old age_max) reaching the API.
  const age_max = t.advantage_audience === 1 ? Math.max(65, t.age_max) : t.age_max;

  return {
    geo_locations: Object.keys(geo_locations).length ? geo_locations : { countries: ['AE'] },
    age_min: t.age_min,
    age_max,
    genders: t.genders.length ? t.genders : undefined,
    interests: t.interests.length ? t.interests : undefined,
    custom_audiences: t.custom_audiences.length ? t.custom_audiences : undefined,
    excluded_custom_audiences: t.excluded_custom_audiences.length ? t.excluded_custom_audiences : undefined,
    // Sent as the legacy top-level field; backend translates it to the
    // `targeting_automation: { advantage_audience }` shape Meta now requires
    // (see fb-ad-sets.service.ts buildTargeting). Don't move the rename here
    // or the backend's translator reads undefined and defaults to 0.
    advantage_audience: t.advantage_audience,
  };
}
